import { useCallback } from 'react';

import { CompanyLegalBeneficiary } from '../../../../../../../../__generated__/GQL';
import { apolloClient } from '../../../../../../../../common/graphql/clients';
import useCompanyContext from '../../../../../../../../common/hooks/useCompanyContext';
import useErrorHandledMutation, {
  throwOnlyUnhandledErrors,
} from '../../../../../../../../common/hooks/useErrorHandledMutation';
import useToast from '../../../../../../../../components/ToastProvider/useToast';
import useSendCompanyInvitation from '../../../../../../common/useSendCompanyInvitation';
import { BeneficiaryCardFormSchemaValid } from '../../../../../../legacy/Pages/BeneficiariesDocs/BeneficiaryCard/formSchema';
import {
  beneficiariesDocsStatusQuery,
  updateCompanyInvitationEmailAddressMutation,
} from '../../../../../../legacy/Pages/BeneficiariesDocs/graphql';
import { UpdateCompanyInvitationEmailVariables } from '../../../../../../legacy/Pages/BeneficiariesDocs/types';
import locales from './locales';

interface useInvitationActionsProps {
  updateCallback: () => void;
  beneficiary: CompanyLegalBeneficiary;
}
const useInvitationActions = ({
  beneficiary,
  updateCallback,
}: useInvitationActionsProps) => {
  const { companyProfileId } = useCompanyContext();
  const { showToast } = useToast();

  if (beneficiary.invitation === null) {
    throw new Error('Beneficiary unexpectedly missing the invitation field');
  }
  if (companyProfileId === undefined) {
    throw new Error('companyProfileId should be defined');
  }
  if (beneficiary.invitation.companyInvitationId === undefined) {
    throw new Error('companyInvitationId should be defined');
  }

  const { firstName, invitation, lastName } = beneficiary;
  const { companyInvitationId } = invitation;

  const showSuccessToast = useCallback(async () => {
    showToast({
      message: `${locales.formatString(locales.linkResendInvitationSuccess, {
        name: `${firstName} ${lastName}`,
      })}`,
      type: 'success',
    });
  }, [firstName, lastName, showToast]);

  const {
    loading: resendInvitationLoading,
    sendCompanyInvitation: sendCompanyInvitationMutation,
  } = useSendCompanyInvitation({
    awaitRefetchQueries: true,
    refetchQueries: () => [
      {
        query: beneficiariesDocsStatusQuery,
        variables: {
          companyProfileId,
        },
      },
    ],
  });

  const [updateInvitationEmailMutator, { loading: isUpdatingInvitationEmail }] =
    useErrorHandledMutation<never, UpdateCompanyInvitationEmailVariables>(
      updateCompanyInvitationEmailAddressMutation,
    );

  const resend = useCallback(async () => {
    await sendCompanyInvitationMutation({
      companyInvitationId,
      companyProfileId,
    });

    showSuccessToast();
  }, [
    sendCompanyInvitationMutation,
    companyInvitationId,
    companyProfileId,
    showSuccessToast,
  ]);

  const update = useCallback(
    async (formValues: BeneficiaryCardFormSchemaValid) => {
      await updateInvitationEmailMutator({
        // We must update the cache here due to a race condition with the company-invitations-emails-function CF
        // If we refresh the beneficiariesDocsStatusQuery then the canResendInvitation bool will always return as true, even though it should not be the case.
        // This is because the update function in the companies service does not send the email directly, so the lastEmailSent timestamp will be set to null.
        // In GraphQL, we are using the lastEmailSent timestamp to set the canResendInvite bool, only enabling it if 24h has passed.
        // A potential side effect here is that the CF will potentially fail silently. In this case the next time the user visits the page they would be able to resend the email as usual.
        onCompleted: () => {
          const beneficiariesDocsStatusQueryData = apolloClient.readQuery({
            query: beneficiariesDocsStatusQuery,
            variables: { companyProfileId },
          });

          apolloClient.writeQuery({
            data: {
              ...beneficiariesDocsStatusQueryData,
              companyLegalBeneficiaries:
                beneficiariesDocsStatusQueryData.companyLegalBeneficiaries.map(
                  (companyLegalBeneficiary: CompanyLegalBeneficiary) => {
                    if (
                      companyLegalBeneficiary.invitation &&
                      companyLegalBeneficiary.invitation.companyInvitationId ===
                        companyInvitationId
                    ) {
                      return {
                        ...companyLegalBeneficiary,
                        invitation: {
                          ...companyLegalBeneficiary.invitation,
                          canResendInvite: false,
                          inviteeEmail: formValues.email,
                        },
                      };
                    }

                    return companyLegalBeneficiary;
                  },
                ),
            },
            query: beneficiariesDocsStatusQuery,
            variables: { companyProfileId },
          });
        },

        variables: {
          input: {
            companyInvitationId,
            companyProfileId,
            inviteeEmail: formValues.email,
          },
        },
      }).catch(throwOnlyUnhandledErrors);

      showSuccessToast();
      updateCallback();
    },
    [
      companyInvitationId,
      companyProfileId,
      updateInvitationEmailMutator,
      updateCallback,
      showSuccessToast,
    ],
  );

  return {
    loading: isUpdatingInvitationEmail || resendInvitationLoading,
    resend,
    update,
  };
};

export default useInvitationActions;
